import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import toast from "react-hot-toast";
import {
  PlusCircle,
  Package,
  ShoppingBag,
  Tag,
  Pencil,
  Trash2,
  Eye,
  TrendingUp,
  CheckCheck,
  Radio,
} from "lucide-react";
import ConfirmModal from "../../components/ui/ConfirmModal";
import "./Dashboard.css";

const CATEGORY_LABELS = {
  notes: "Notes",
  gaming: "Gaming",
  clothing: "Clothing",
  electronics: "Electronics",
  books: "Books",
  sports: "Sports",
  food: "Food",
  other: "Other",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  // Modal state
  const [modal, setModal] = useState({
    open: false,
    type: null, // 'sold' | 'delete'
    productId: null,
    productTitle: "",
  });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const { data } = await api.get("/products/my-listings");
      setListings(data.products);
    } catch {
      toast.error("Could not load your listings.");
    } finally {
      setLoading(false);
    }
  };

  const totalReach = listings.reduce(
    (sum, p) => sum + (p.contactCount || 0),
    0,
  );

  // Open modal helpers
  const openSoldModal = (id, title) =>
    setModal({ open: true, type: "sold", productId: id, productTitle: title });
  const openDeleteModal = (id, title) =>
    setModal({
      open: true,
      type: "delete",
      productId: id,
      productTitle: title,
    });
  const closeModal = () =>
    setModal({ open: false, type: null, productId: null, productTitle: "" });

  // Confirm actions
  const handleConfirm = async () => {
    const { type, productId } = modal;
    closeModal();

    if (type === "sold") {
      setActing(productId + "_sold");
      try {
        await api.put("/products/" + productId + "/sold");
        setListings((l) =>
          l.map((p) => (p._id === productId ? { ...p, status: "sold" } : p)),
        );
        toast.success("Marked as sold! Sell count updated 🎉");
      } catch (err) {
        toast.error(err.response?.data?.message || "Server error. Try again.");
      } finally {
        setActing(null);
      }
    }

    if (type === "delete") {
      setActing(productId + "_del");
      try {
        await api.delete("/products/" + productId);
        setListings((l) => l.filter((p) => p._id !== productId));
        toast.success("Listing removed.");
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to remove.");
      } finally {
        setActing(null);
      }
    }
  };

  const activeListings = listings.filter((p) => p.status === "active");
  const soldListings = listings.filter((p) => p.status === "sold");
  const removedListings = listings.filter(
    (p) => p.status === "removed_by_seller" || p.status === "removed_by_admin",
  );

  return (
    <div className="dashboard fade-in">
      {/* Glassmorphism confirm modal */}
      <ConfirmModal
        isOpen={modal.open}
        onCancel={closeModal}
        onConfirm={handleConfirm}
        title={modal.type === "sold" ? "Mark as Sold?" : "Remove Listing?"}
        message={
          modal.type === "sold"
            ? '"' +
              modal.productTitle +
              '" will be marked as sold and your sell count will increase.'
            : 'Remove "' + modal.productTitle + '" from your active listings?'
        }
        confirmText={modal.type === "sold" ? "Yes, Mark Sold" : "Remove"}
        confirmClass={modal.type === "sold" ? "btn-success" : "btn-danger"}
      />

      {/* Header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-welcome">
            Hey, {user && user.name ? user.name.split(" ")[0] : ""} 👋
          </h1>
          <p
            className="text-muted"
            style={{ marginTop: 4, fontSize: "0.875rem" }}
          >
            {user ? user.rollNumber : ""} · {user ? user.university : ""}
          </p>
        </div>
        {/* ✅ Fixed: added /app prefix */}
        <Link to="/app/sell" className="btn btn-primary">
          <PlusCircle size={16} /> List Item
        </Link>
      </div>

      {/* Stats */}
      <div className="dash-stats">
        {[
          {
            icon: Tag,
            label: "Active Listings",
            value: user ? user.activeListings || 0 : 0,
            color: "var(--accent)",
            bg: "var(--accent-soft)",
          },
          {
            icon: TrendingUp,
            label: "Total Sells",
            value: user ? user.sellCount || 0 : 0,
            color: "var(--success)",
            bg: "var(--success-soft)",
          },
          {
            icon: Radio,
            label: "Total Reach",
            value: totalReach,
            color: "var(--info)",
            bg: "var(--info-soft)",
          },
          {
            icon: ShoppingBag,
            label: "Items Contacted",
            value: user ? user.buyCount || 0 : 0,
            color: "var(--warning)",
            bg: "var(--warning-soft)",
          },
        ].map((s) => (
          <div key={s.label} className="dash-stat-card card">
            <div
              className="dash-stat-icon"
              style={{ background: s.bg, color: s.color }}
            >
              <s.icon size={18} />
            </div>
            <div className="dash-stat-value">{s.value}</div>
            <div className="dash-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Active listings */}
      <section className="dash-section">
        <div className="dash-section-header">
          <h3>
            Active Listings
            <span
              className="badge badge-success"
              style={{ fontSize: "0.75rem", marginLeft: 8 }}
            >
              {activeListings.length}
            </span>
          </h3>
          {/* ✅ Fixed: added /app prefix */}
          <Link to="/app/sell" className="btn btn-secondary btn-sm">
            <PlusCircle size={13} /> New
          </Link>
        </div>

        {loading ? (
          <div className="loading-center">
            <div className="spinner" />
          </div>
        ) : activeListings.length === 0 ? (
          <div
            className="empty-state card"
            style={{ padding: "var(--space-8)" }}
          >
            <div className="empty-state-icon">📦</div>
            <h4>No active listings</h4>
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              List something to sell — notes, gadgets, clothing!
            </p>
            {/* ✅ Fixed: added /app prefix */}
            <Link
              to="/app/sell"
              className="btn btn-primary"
              style={{ marginTop: 8 }}
            >
              <PlusCircle size={15} /> List an Item
            </Link>
          </div>
        ) : (
          <div className="dash-listings">
            {activeListings.map((product) => (
              <ProductRow
                key={product._id}
                product={product}
                onDelete={() => openDeleteModal(product._id, product.title)}
                onMarkSold={() => openSoldModal(product._id, product.title)}
                acting={acting}
              />
            ))}
          </div>
        )}
      </section>

      {/* Sold */}
      {soldListings.length > 0 && (
        <section className="dash-section">
          <div className="dash-section-header">
            <h3 style={{ color: "var(--success)" }}>
              Sold
              <span
                className="badge badge-success"
                style={{ fontSize: "0.75rem", marginLeft: 8 }}
              >
                {soldListings.length}
              </span>
            </h3>
          </div>
          <div className="dash-listings">
            {soldListings.map((product) => (
              <ProductRow
                key={product._id}
                product={product}
                acting={acting}
                sold
              />
            ))}
          </div>
        </section>
      )}

      {/* Removed */}
      {removedListings.length > 0 && (
        <section className="dash-section">
          <div className="dash-section-header">
            <h3 style={{ color: "var(--text-muted)" }}>
              Removed
              <span
                className="badge badge-neutral"
                style={{ fontSize: "0.75rem", marginLeft: 8 }}
              >
                {removedListings.length}
              </span>
            </h3>
          </div>
          <div className="dash-listings">
            {removedListings.map((product) => (
              <ProductRow
                key={product._id}
                product={product}
                acting={acting}
                removed
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Product row with icon + label buttons ─────────────────
function ProductRow({ product, onDelete, onMarkSold, acting, removed = false, sold = false }) {
  const navigate = useNavigate();

  // Days since listing created
  const daysSince = Math.floor(
    (Date.now() - new Date(product.createdAt).getTime()) / (1000 * 60 * 60 * 24)
  );
  const daysLeft = 10 - daysSince;

  const statusMap = {
    active:            { text: "Active",           cls: "badge-success" },
    sold:              { text: "Sold",              cls: "badge-info"    },
    removed_by_seller: { text: "Removed",          cls: "badge-neutral" },
    removed_by_admin:  { text: "Removed by admin", cls: "badge-danger"  },
  };
  const statusInfo = statusMap[product.status] || { text: product.status, cls: "badge-neutral" };

  return (
    <div className={"product-row card" + ((removed || sold) ? " product-row-faded" : "")}>

      {/* Image */}
      <div className="product-row-img">
        {product.images && product.images[0]
          ? <img src={product.images[0]} alt={product.title} />
          : <div className="product-row-img-placeholder"><Package size={20} /></div>
        }
      </div>

      {/* Info */}
      <div className="product-row-info">
        <div className="product-row-top">
          <span className="product-row-title">{product.title}</span>
          <span className={"badge " + statusInfo.cls}>{statusInfo.text}</span>
        </div>

        <div className="product-row-meta">
          <span className="product-row-price">₹{product.price.toLocaleString()}</span>
          <span className={"badge badge-" + product.category}>
            {CATEGORY_LABELS[product.category] || product.category}
          </span>
          <span className="reach-chip">
            👁 {product.contactCount || 0} reach
          </span>
        </div>

        {/* Expiry warning */}
        {product.status === "active" && daysLeft <= 3 && daysLeft > 0 && (
          <div className="expiry-warning">
            ⏰ Expires in {daysLeft} day{daysLeft !== 1 ? "s" : ""}
          </div>
        )}

        {product.status === "removed_by_admin" && product.removalReason && (
          <p className="product-row-removal-reason">
            Reason: {product.removalReason}
          </p>
        )}
      </div>

      {/* Actions */}
      {!removed && !sold && (
        <div className="product-row-actions">
          {/* ✅ Fixed: added /app prefix to both navigate calls */}
          <button className="row-action-btn"
            onClick={() => navigate("/app/product/" + product._id)} title="View">
            <Eye size={16} /><span>View</span>
          </button>
          <button className="row-action-btn"
            onClick={() => navigate("/app/edit-product/" + product._id)} title="Edit">
            <Pencil size={16} /><span>Edit</span>
          </button>
          <button className="row-action-btn success"
            onClick={onMarkSold}
            disabled={acting === product._id + "_sold"} title="Mark as Sold">
            {acting === product._id + "_sold"
              ? <div className="spinner" style={{ width: 15, height: 15 }} />
              : <CheckCheck size={16} />
            }
            <span>Sold</span>
          </button>
          <button className="row-action-btn danger"
            onClick={onDelete}
            disabled={acting === product._id + "_del"} title="Remove">
            {acting === product._id + "_del"
              ? <div className="spinner" style={{ width: 15, height: 15 }} />
              : <Trash2 size={16} />
            }
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
}